import { EMPTY_LINKS } from "@/components/wizard/LinksSection";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/layout/AppShell";
import { useAdmin } from "@/components/auth/AdminGate";
import StepCompany from "@/components/wizard/StepCompany";
import StepType from "@/components/wizard/StepType";
import StepVisual from "@/components/wizard/StepVisual";
import StepMaterials from "@/components/wizard/StepMaterials";
import WizardAssistant from "@/components/wizard/WizardAssistant";
import { cleanItems } from "@/components/catalog/CatalogEditor";
import GenerateCostDialog from "@/components/ai/GenerateCostDialog";
import { INTERNAL } from "@/components/sites/ClientPicker";
import { DraftBanner, clearDraft, useWizardDraft } from "@/components/wizard/WizardDraft";
import { errMsg, useApi } from "@/lib/api";

const STEPS = [
  { title: "Azienda", sub: "Informazioni e obiettivo", C: StepCompany },
  { title: "Progetto", sub: "Tipologia e partenza", C: StepType },
  { title: "Stile", sub: "Preferenze visive", C: StepVisual },
  { title: "Materiali", sub: "Logo, immagini, documenti", C: StepMaterials },
];

export default function NewProject() {
  const api = useApi();
  const navigate = useNavigate();
  const { settings } = useAdmin();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState("");
  const [genFor, setGenFor] = useState(null);
  const [form, setForm] = useState({
    company_name: "", name: "", name_auto: true, company_legal: "", sector: "", website_url: "", description: "", services: "", audience: "", goal: "", required_pages: "",
    language: settings?.generation?.default_language || "it", project_types: ["sito_vetrina"], start_mode: "ai", template_id: null, scraped: null, client_images_authorized: false, client_id: null, catalog: [], decisions: [],
    visual: { primary_color: "", secondary_colors: "", font: "", theme: "", styles: [], references: "", notes: "" }, files: [], links: EMPTY_LINKS,
  });
  const set = (patch) => setForm((f) => ({ ...f, ...(typeof patch === "function" ? patch(f) : patch) }));
  const draft = useWizardDraft(form, step, (f, s) => { setForm((cur) => ({ ...cur, ...f, files: [] })); setStep(s || 0); });
  const valid = [form.company_name.trim() && form.name.trim() && form.client_id, form.project_types.length > 0 && (form.start_mode !== "template" || form.template_id), true, true];
  const { C } = STEPS[step];

  const submit = async () => {
    try {
      setBusy("Creazione del progetto…");
      const { files, client_id, name_auto, company_legal, catalog, decisions, draft_id, suggestions, ...payload } = form; // eslint-disable-line no-unused-vars
      const { data: p } = await api.post("/projects", { ...payload, client_id: client_id === INTERNAL ? "" : client_id });
      clearDraft();
      if (draft_id) { try { await api.post(`/wizard/drafts/${draft_id}/migrate`, { project_id: p.id }); } catch (e) { toast.error(`Conversazione: ${errMsg(e)}`); } }
      const rows = cleanItems(catalog || []);
      if (rows.length) { setBusy("Salvataggio di servizi e prodotti…"); try { await api.put(`/projects/${p.id}/catalog`, { items: rows }); } catch (e) { toast.error(`Servizi e prodotti: ${errMsg(e)}`); } }
      if ((decisions || []).length) { try { await api.post(`/projects/${p.id}/brief-chat/import`, { decisions }); } catch (e) { toast.error(`Decisioni: ${errMsg(e)}`); } }
      for (let i = 0; i < files.length; i++) {
        setBusy(`Caricamento file ${i + 1}/${files.length}…`);
        const fd = new FormData(); fd.append("file", files[i].file); fd.append("kind", files[i].kind);
        try { await api.post(`/projects/${p.id}/assets`, fd); } catch (e) { toast.error(`${files[i].file.name}: ${errMsg(e)}`); }
      }
      if (form.start_mode === "ai") { setBusy("Stima del costo AI…"); setGenFor(p.id); return; }
      toast.success("Progetto creato");
      navigate(`/projects/${p.id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const skipGeneration = () => {
    toast.message("Sito creato senza generazione AI", { description: "Il brief è salvato. Nell'editor trovi «Modifica brief» e «Genera sito completo» (anche nel pannello «Impostazioni progetto»)." });
    navigate(`/projects/${genFor}`);
  };

  return (
    <AppShell>
      <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-6xl">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="wizard-back-link"><ArrowLeft size={15} />Siti web</Link>
        <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Nuovo sito</h1>
        <p className="mt-2 text-sm text-slate-500">Compila il brief: più è preciso, migliore sarà la demo. Viene salvato in automatico in questo browser finché non crei il sito.</p>
        <DraftBanner draft={draft} />
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[220px_1fr]">
          <ol className="flex min-w-0 lg:flex-col gap-2 overflow-x-auto" data-testid="wizard-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <button onClick={() => i <= step || valid.slice(0, i).every(Boolean) ? setStep(i) : null} data-testid={`wizard-step-${i}`}
                  className={`flex items-center gap-3 text-left rounded-lg px-3 py-2.5 w-full whitespace-nowrap transition-colors ${i === step ? "bg-white border border-slate-200 shadow-sm" : "hover:bg-white/60"}`}>
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${i < step ? "bg-sky-600 text-white" : i === step ? "bg-navy-900 text-white" : "bg-slate-200 text-slate-600"}`}>{i < step ? <Check size={14} /> : i + 1}</span>
                  <span><span className="block text-sm font-medium text-slate-900">{s.title}</span><span className="hidden lg:block text-[11px] text-slate-500">{s.sub}</span></span>
                </button>
              </li>
            ))}
          </ol>
          <section className="min-w-0 bg-white/60 lg:bg-transparent rounded-xl">
            <C form={form} set={set} />
            <div className="mt-6"><WizardAssistant api={api} form={form} set={set} /></div>
            <div className="mt-10 pt-6 border-t flex items-center justify-between">
              <Button variant="ghost" disabled={step === 0 || !!busy} onClick={() => setStep(step - 1)} data-testid="wizard-prev-btn"><ArrowLeft size={15} className="mr-1.5" />Indietro</Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!valid[step]} className="bg-navy-900 hover:bg-navy-800" data-testid="wizard-next-btn">Avanti<ArrowRight size={15} className="ml-1.5" /></Button>
              ) : (
                <Button onClick={submit} disabled={!!busy || !valid[0] || !valid[1]} className="bg-sky-600 hover:bg-sky-700" data-testid="wizard-submit-btn">
                  {busy ? <><Loader2 size={15} className="mr-1.5 animate-spin" />{busy}</> : form.start_mode === "ai" ? <><Sparkles size={15} className="mr-1.5" />Genera demo</> : "Crea progetto"}
                </Button>
              )}
            </div>
            {!valid[0] && step > 0 && <p className="mt-3 text-xs text-red-600">Nel primo passaggio scegli il cliente e indica il nome del progetto{form.client_id === INTERNAL ? " e il nome azienda" : ""}.</p>}
          </section>
        </div>
      </div>
      {genFor && (
        <GenerateCostDialog open onOpenChange={(o) => !o && skipGeneration()} api={api} projectId={genFor} cancelLabel="Non generare ora"
          title="Avviare la generazione AI?" description="Il progetto è stato creato con il brief e i materiali. Conferma il costo stimato per generare la demo."
          onStarted={() => { toast.success("Progetto creato: generazione avviata"); navigate(`/projects/${genFor}`); }} />
      )}
    </AppShell>
  );
}
